<template>
  <div class="row">
    <div class="col-12">
      <div class="ads-page-header d-md-flex align-items-center justify-content-between mb-20">
        <div class="d-flex align-items-center">
          <span class="ads-page-icon">
            <i class="flaticon-placeholder"></i>
          </span>
          <div class="ms-12">
            <h4 class="mb-0 fw-bold">Gestion des agences</h4>
            <p class="mb-0 text-muted fs-13">
              {{ agencies.length }} agence{{ agencies.length > 1 ? "s" : "" }} au total
            </p>
          </div>
        </div>
      </div>

      <div class="card ads-card mb-25 border-0 letter-spacing">
        <div
          class="card-head d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
        >
          <div class="d-sm-flex align-items-center">
            <button
              class="ads-btn-primary position-relative transition border-0 fw-medium text-white d-inline-flex align-items-center me-10 mb-10 mb-lg-0"
              type="button"
              @click="openCreateModal"
              v-if="auth.can('SETTINGS')"
            >
              <i class="flaticon-plus position-relative fs-12 me-8"></i>
              Nouvelle agence
            </button>
            <button
              class="ads-btn-outline position-relative transition fw-medium d-inline-flex align-items-center mb-10 mb-lg-0"
              type="button"
            >
              <i class="flaticon-file-1 position-relative fs-15 me-8"></i>
              Export
            </button>
          </div>
          <div class="ads-search position-relative">
            <i class="flaticon-search ads-search-icon"></i>
            <input
              type="text"
              class="form-control ps-40"
              v-model="searchTerm"
              placeholder="Rechercher une agence..."
            />
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25 pt-0">
          <EasyDataTable
            :headers="headers"
            :items="agencies"
            :loading="loading"
            :search-value="searchTerm"
            :theme-color="themeColor"
            alternating
            table-class-name="ads-table"
            buttons-pagination
          >
            <template #loading>
              <div class="skeleton-table w-100">
                <div class="loading-spinner-wrap text-center py-15">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Chargement...</span>
                  </div>
                </div>
                <div
                  class="skeleton-row d-flex align-items-center"
                  v-for="n in 6"
                  :key="n"
                >
                  <div
                    class="skeleton-cell"
                    v-for="c in headers.length"
                    :key="c"
                  >
                    <span class="skeleton-bar"></span>
                  </div>
                </div>
              </div>
            </template>

            <template #empty-message>
              <div class="empty-state text-center py-40">
                <i class="flaticon-placeholder fs-40 d-block mb-10 text-muted"></i>
                <p class="mb-0 text-muted">Aucune agence trouvée</p>
              </div>
            </template>

            <!-- Custom "active" column (status badge) -->
            <template #item-active="{ id, active }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-active-' + id"
                  :checked="active"
                  @change="toggleAgencyActive(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-active-' + id"></label>
              </div>
            </template>

            <template #item-ramassage="{ id, ramassage }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-ramassage-' + id"
                  :checked="ramassage"
                  @change="toggleAgencieRamassage(id, $event.target.checked)"
                />
                <label
                  class="switch-label"
                  :for="'switch-ramassage-' + id"
                ></label>
              </div>
            </template>

            <template #item-auto_distribution="{ id, auto_distribution }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-distribution-' + id"
                  :checked="auto_distribution"
                  @change="toggleAgencieDistribution(id, $event.target.checked)"
                />
                <label
                  class="switch-label"
                  :for="'switch-distribution-' + id"
                ></label>
              </div>
            </template>

            <template #item-stock="{ id, stock }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-stock-' + id"
                  :checked="stock"
                  @change="toggleAgencieStock(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-stock-' + id"></label>
              </div>
            </template>

            <!-- Custom "actions" column -->
            <template #item-actions="{ id, item }">
              <div class="d-flex align-items-center gap-8" v-if="auth.can('SETTINGS')">
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-edit"
                  title="Modifier"
                  @click="openEditModal(id, item)"
                >
                  <i class="flaticon-pen"></i>
                </button>
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-delete"
                  title="Supprimer"
                  @click="deleteAgency(id)"
                >
                  <i class="flaticon-delete"></i>
                </button>
              </div>
            </template>
          </EasyDataTable>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <div
    class="modal fade"
    id="createnewAgencyModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content ads-modal-content">
        <div class="modal-header ads-modal-header">
          <div class="d-flex align-items-center">
            <span class="ads-modal-icon">
              <i class="flaticon-placeholder"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">
              {{ isEditing ? "Modifier Agence" : "Nouvelle Agence" }}
            </h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submitAgency">
            <div class="row g-10">
              <!-- Nom -->
              <div class="col-12">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Nom d'agence</label>
                  <input
                    type="text"
                    v-model="newAgency.name"
                    class="form-control"
                  />
                </div>
              </div>

              <!-- Email -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Email</label>
                  <input
                    type="email"
                    v-model="newAgency.email"
                    class="form-control"
                  />
                </div>
              </div>

              <!-- Téléphone -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Téléphone</label>
                  <input
                    type="text"
                    v-model="newAgency.phone"
                    class="form-control"
                  />
                </div>
              </div>

              <!-- Adresse -->
              <div class="col-12">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Adresse</label>
                  <textarea
                    v-model="newAgency.adresse"
                    rows="2"
                    class="form-control"
                  ></textarea>
                </div>
              </div>

              <!-- Zone -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Zone</label>
                  <v-select
                    class="custom-select-height"
                    v-model="newAgency.zone"
                    label="name"
                    :options="zones"
                    @change="getCityZone"
                  />
                </div>
              </div>

              <!-- Ville -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Ville</label>
                  <v-select
                    class="custom-select-height"
                    v-model="newAgency.city"
                    label="name"
                    :options="citiesList"
                    :disabled="active"
                  />
                </div>
              </div>

              <template v-if="!isEditing">
                <!-- Stock -->
                <div class="col-md-6">
                  <div class="form-group mb-20">
                    <label class="fw-medium mb-10 d-block">Stock</label>
                    <select
                      class="form-select"
                      v-model="newAgency.stock"
                    >
                      <option v-for="s in YesNo" :key="s.id" :value="s">
                        {{ s.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Auto distribution -->
                <div class="col-md-6">
                  <div class="form-group mb-20">
                    <label class="fw-medium mb-10 d-block">Auto distribution</label>
                    <select
                      class="form-select"
                      v-model="newAgency.distribution"
                    >
                      <option v-for="d in YesNo" :key="d.id" :value="d">
                        {{ d.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Ramassage -->
                <div class="col-12">
                  <div class="form-group mb-20">
                    <label class="fw-medium mb-10 d-block">Ramassage</label>
                    <select
                      class="form-select"
                      v-model="newAgency.ramassage"
                    >
                      <option v-for="r in YesNo" :key="r.id" :value="r">
                        {{ r.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Agence d'envoie -->
                <div class="col-md-6">
                  <div class="form-group mb-20">
                    <label class="fw-medium mb-10 d-block">Agence d'envoie</label>
                    <select
                      class="form-select"
                      v-model="newAgency.send"
                    >
                      <option v-for="se in YesNo" :key="se.id" :value="se">
                        {{ se.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Reception Principale -->
                <div class="col-md-6">
                  <div class="form-group mb-20">
                    <label class="fw-medium mb-10 d-block">Reception Principale</label>
                    <select
                      class="form-select"
                      v-model="newAgency.principal"
                    >
                      <option v-for="p in YesNo" :key="p.id" :value="p">
                        {{ p.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </template>
            </div>
          </form>
        </div>

        <div class="modal-footer ads-modal-footer">
          <button
            type="button"
            class="ads-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Annuler
          </button>
          <button type="button" @click="submitAgency" class="ads-btn-primary border-0 transition fw-medium">
            {{ isEditing ? "Mettre à jour" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Modal } from "bootstrap";
import { onMounted, reactive, ref } from "vue";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";

export default {
  name: "AgenciesContent",
  setup: () => {
    const auth = useAuthStore();
    const { showToast } = useAppToast();
    const themeColor = "#06b48a";
    const searchTerm = ref("");

    const newAgency = reactive({
      name: "",
      email: "",
      phone: "",
      adresse: "",
      zone: null,
      city: null,
      stock: null,
      distribution: null,
      ramassage: null,
    });
    const isEditing = ref(false);
    const headers = [
      { text: "Nom", value: "name" },
      { text: "Zone", value: "zone_name" },
      { text: "Ville", value: "agency_name" },
      { text: "Statut", value: "active" },
      { text: "Stock", value: "stock" },
      { text: "Ramassage", value: "ramassage" },
      { text: "AutoDistribution", value: "auto_distribution" },
      { text: "Actions", value: "actions" },
    ];

    const agencies = ref([]);
    const zones = ref([]);
    const loading = ref(true);

    const citiesList = ref([]);
    const YesNo = ref([
      { id: 1, name: "Oui" },
      { id: 0, name: "Non" },
    ]);
    const active = ref(false);

    onMounted(async () => {
      fetchAgencies();
      fetchZones();
    });

    function resetForm() {
      Object.assign(newAgency, {
        name: "",
        email: "",
        phone: "",
        adresse: "",
        zone: null,
        city: null,
        stock: null,
        distribution: null,
        ramassage: null,
      });
    }

    function openCreateModal() {
      isEditing.value = false;
      resetForm();

      const modalEl = document.getElementById("createnewAgencyModal");
      const modal = new Modal(modalEl);
      modal.show();
    }

    function openEditModal(id, agency) {
      const rowAgency = agencies.value.find((c) => c.id === id);
      if (rowAgency) {
        isEditing.value = true;
        Object.assign(newAgency, rowAgency);
        console.log("Editing agency:", rowAgency);

        const modalEl = document.getElementById("createnewAgencyModal");
        const modal = new Modal(modalEl);
        modal.show();
      }
    }

    async function submitAgency() {
      if (!newAgency.zone || !newAgency.name || !newAgency.city) {
        showToast("Please fill in all fields!", "error");
        return;
      }

      if (isEditing.value) {
        try {
          // Example: send to API
          const response = await api.post(
            "/api/auth/agency_edit/" + newAgency.id,
            {
              name: newAgency.name,
              email: newAgency.email,
              adresse: newAgency.adresse,
              phone: newAgency.phone,
              id_zone: newAgency.zone.id,
              zone_name: newAgency.zone.name,
              id_city: newAgency.city.id,
              city_name: newAgency.city.name,
            }
          );

          if (response.data.message == "success") {
            const modalEl = document.getElementById("createnewAgencyModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            // get agencies list refresh
            fetchAgencies();
            showToast("Zone added successfully!", "success");
          } else {
            showToast("Error: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while adding the agency.", "error");
        }
        console.log("Editing agency:", newAgency);
      } else {
        try {
          console.log("Adding new agency:", newAgency);
          // Example: send to API
          const response = await api.post("/api/auth/agency_add", {
            name: newAgency.name,
            email: newAgency.email,
            adresse: newAgency.adresse,
            phone: newAgency.phone,
            distribution: newAgency.distribution.id,
            ramassage: newAgency.ramassage.id,
            stock: newAgency.stock.id,
            id_zone: newAgency.zone.id,
            zone_name: newAgency.zone.name,
            id_city: newAgency.city.id,
            city_name: newAgency.city.name,
          });

          if (response.data.message == "success") {
            const modalEl = document.getElementById("createnewAgencyModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            // get agencies list refresh
            fetchAgencies();
            showToast("Zone added successfully!", "success");
          } else {
            showToast("Error: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while adding the agency.", "error");
        }
      }
    }

    async function fetchAgencies() {
      loading.value = true;
      try {
        const response = await api.get("/api/auth/fetch_agency_list");
        if (response && response.data) {
          // handle the fetched agencies data
          // console.log("Fetched agencies:", response.data);
          agencies.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching agencies:", error);
      } finally {
        loading.value = false;
      }
    }
    async function fetchZones() {
      try {
        const response = await api.get("/api/auth/ListZone");
        if (response && response.data) {
          // handle the fetched agencies data
          // console.log("Fetched agencies:", response.data);
          zones.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching agencies:", error);
      }
    }

    async function getCityZone() {
      console.log("Selected zone:", newAgency.zone);
      try {
        const response = await api.get(
          "/api/auth/CitiesZone/" + newAgency.zone.id
        );
        if (response && response.data) {
          // handle the fetched agencies data
          // console.log("Fetched agencies:", response.data);
          citiesList.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching agencies:", error);
      }
    }

    async function toggleAgencyActive(id, value) {
      const agency = agencies.value.find((z) => z.id === id);
      if (agency) {
        agency.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/agency_active/${id}`, {
          active: value,
        });
        if (res.message == "success") {
          showToast("Agence est bien modifiée", "success");
        }
        console.log("Zone active status updated:", res.data);
      }
    }

    async function toggleAgencieRamassage(id, value) {
      const agency = agencies.value.find((z) => z.id === id);
      if (agency) {
        agency.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/rmsg_active/${id}`, {
          ramassage: value,
        });
        if (res.message == "success") {
          showToast("Agence est bien modifiée", "success");
        }
        console.log("Zone active status updated:", res.data);
      }
    }

    async function toggleAgencieDistribution(id, value) {
      const agency = agencies.value.find((z) => z.id === id);
      if (agency) {
        agency.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/distribu_active/${id}`, {
          auto_distribution: value,
        });
        if (res.message == "success") {
          showToast("Agence est bien modifiée", "success");
        }
        console.log("Zone active status updated:", res.data);
      }
    }

    async function toggleAgencieStock(id, value) {
      const agency = agencies.value.find((z) => z.id === id);
      if (agency) {
        agency.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/stock_active/${id}`, {
          stock: value,
        });
        if (res.message == "success") {
          showToast("Agence est bien modifiée", "success");
        }
        console.log("Zone active status updated:", res.data);
      }
    }

    async function deleteAgency(id) {
      Swal.fire({
        text: "Vous voulez supprimer cette agence ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Oui, supprimer!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await api.post(`/api/auth/removeAgency/${id}`);
            if (response.data.message === "success") {
              // Remove the agency from the local list
              fetchAgencies();
              showToast("Agency deleted successfully!", "success");
            } else {
              showToast(
                "Error deleting agency: " + response.data.message,
                "error"
              );
            }
          } catch (error) {
            console.error("Error deleting agency:", error);
            showToast("An error occurred while deleting the agency.", "error");
          }
        }
      });
    }

    return {
      newAgency,
      submitAgency,
      headers,
      agencies,
      zones,
      loading,
      themeColor,
      searchTerm,
      toggleAgencyActive,
      toggleAgencieRamassage,
      toggleAgencieDistribution,
      toggleAgencieStock,
      openCreateModal,
      openEditModal,
      isEditing,
      deleteAgency,
      YesNo,
      citiesList,
      getCityZone,
      auth,
    };
  },
};
</script>

<style scoped>
/* ---------- Page header ---------- */
.ads-page-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  font-size: 20px;
}

/* ---------- Card ---------- */
.ads-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(43, 42, 63, 0.06);
}

.ads-card .card-head {
  border-bottom: 1px solid #f0f0f5;
}

/* ---------- Buttons ---------- */
.ads-btn-primary {
  background: #06b48a;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-primary:hover {
  background: #059c78;
  color: #fff;
}

.ads-btn-outline {
  background: transparent;
  border: 1px solid #e2e2ea;
  color: #2b2a3f;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-outline:hover {
  background: #f8f8fa;
  border-color: #d5d5e0;
}

/* ---------- Search ---------- */
.ads-search {
  min-width: 260px;
}

.ads-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a1bb;
  font-size: 14px;
}

.ads-search .form-control {
  border-radius: 8px;
  border: 1px solid #e2e2ea;
  height: 44px;
}

.ads-search .form-control:focus {
  border-color: #06b48a;
  box-shadow: 0 0 0 3px rgba(6, 180, 138, 0.12);
}

/* ---------- Table ---------- */
:deep(.ads-table) {
  --easy-table-border: none;
  --easy-table-row-border: 1px solid #f1f1f5;

  --easy-table-header-font-size: 12px;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #8e8da1;
  --easy-table-header-background-color: #f8f8fb;
  --easy-table-header-item-padding: 12px 16px;

  --easy-table-body-row-font-size: 14px;
  --easy-table-body-row-height: 60px;
  --easy-table-body-row-font-color: #2b2a3f;
  --easy-table-body-row-background-color: #ffffff;
  --easy-table-body-item-padding: 12px 16px;

  --easy-table-body-row-hover-font-color: #2b2a3f;
  --easy-table-body-row-hover-background-color: #f8fbfa;

  --easy-table-body-even-row-font-color: #2b2a3f;
  --easy-table-body-even-row-background-color: #fbfbfd;

  --easy-table-footer-font-color: #8e8da1;
  --easy-table-footer-font-size: 13px;
  --easy-table-footer-background-color: #ffffff;

  --easy-table-message-font-color: #8e8da1;

  --easy-table-loading-mask-background-color: #ffffff;
}

:deep(.ads-table th) {
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ---------- Action icon buttons ---------- */
.ads-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #eceef1;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8e8da1;
  transition: all 0.2s ease;
}

.ads-icon-btn-edit:hover {
  background: rgba(101, 96, 240, 0.1);
  border-color: transparent;
  color: #6560f0;
}

.ads-icon-btn-delete:hover {
  background: rgba(239, 41, 41, 0.1);
  border-color: transparent;
  color: #ef2929;
}

/* ---------- Modal ---------- */
.ads-modal-content {
  border: none;
  border-radius: 14px;
  overflow: hidden;
}

.ads-modal-header {
  background: #f8f8fb;
  border-bottom: 1px solid #f0f0f5;
  padding: 18px 25px;
}

.ads-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.ads-modal-footer {
  border-top: 1px solid #f0f0f5;
  padding: 16px 25px;
}

.ads-modal-footer .ads-btn-primary,
.ads-modal-footer .ads-btn-outline {
  padding: 9px 20px;
}

/* ---------- Loading skeleton ---------- */
.skeleton-table {
  padding: 10px 0;
}

.skeleton-row {
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f1;
  gap: 20px;
}

.skeleton-cell {
  flex: 1;
}

.skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e5e9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.empty-state {
  width: 100%;
}
</style>
